/* global Excel, window */

import { canUseScopedApis, filterToSpillAnchors, scanForSheetyBanksFormulas } from "./formulaScan";

export type RefreshResult = { scoped: true; count: number } | { scoped: false };

/**
 * Re-enters every formula in the workbook that calls a SHEETYBANKS function.
 * Re-entering a formula forces that cell to recalculate — even in manual
 * calculation mode — so this refreshes exactly our custom functions without
 * recalculating the rest of the workbook or requiring any trigger argument
 * in user formulas.
 */
export async function refreshSheetyBanksFormulas(): Promise<RefreshResult> {
  // INFO, BRANCHES and MARKET results are cached in the functions runtime; a
  // refresh should re-fetch, so drop the caches before re-entering formulas.
  window.sheetybanksClearCaches?.();

  if (!canUseScopedApis()) {
    await Excel.run(async (context) => {
      context.workbook.application.calculate(Excel.CalculationType.full);
      await context.sync();
    });
    return { scoped: false };
  }

  const count = await Excel.run(async (context) => {
    const candidates = await scanForSheetyBanksFormulas(context);
    if (candidates.length === 0) return 0;

    // A spilled result's formula lives only in its anchor cell; writing it
    // into a spill member would produce #SPILL!, so only anchors are
    // rewritten.
    const anchors = await filterToSpillAnchors(context, candidates);

    for (const { cell, formula } of anchors) {
      cell.formulas = [[formula]];
    }
    const written = anchors.length;

    // Re-entry recalculates our cells, but in manual mode their dependents
    // (e.g. a user's =B2*100) are only marked dirty — a dirty-cell recalc
    // flushes them without recomputing the rest of the workbook. Harmless in
    // automatic mode, where Excel recalculates dependents on its own.
    if (written > 0) {
      context.workbook.application.calculate(Excel.CalculationType.recalculate);
    }
    await context.sync();
    return written;
  });

  return { scoped: true, count };
}
